import produce from "immer";
import { AdminTableTypeEnum } from "src/requests/administration/types";
import {
  AnyIndexDictionary,
  Dictionary,
  NumberIndexedDictionary,
  UserFilter,
  WhereItems,
} from "../globalTypes";
import {
  TableIdentifyingDtoProps,
  UserFilterDTO,
} from "../requests/userFilters/types";
import { TableIdentifier } from "../store/tableData/types";
import { FilterPatch } from "../requests/userFilters";
import { capitalizeString, uncapitalizeString } from "./formatting";

export function getItemDictFromFilters(
  filters: Record<string | number, UserFilter | undefined>
) {
  const filterArr = Object.values(filters);
  const itemDictEntries = filterArr.map((filter) => [
    filter?.id,
    filter?.whereItems,
  ]);
  return Object.fromEntries(itemDictEntries) as AnyIndexDictionary<WhereItems>;
}

type CreateFilterParams = {
  name: string;
  whereItems: WhereItems;
  id: string;
  identifier: TableIdentifier;
  saveForAll: boolean;
};
type CUF = (p: CreateFilterParams) => UserFilter;
export const createUserFilter: CUF = ({
  identifier,
  name,
  id,
  whereItems,
  saveForAll,
}) => ({
  whereItems,
  name,
  tableIdentifier: identifier,
  id,
  active: false,
  image: "",
  isAllUsers: saveForAll,
});

const createTableIdentifierFromFilterDto = (
  dto: UserFilterDTO
): TableIdentifier => {
  if (dto.type === "administration") {
    return {
      kind: "administration",
      idWithinKind: capitalizeString(
        dto.administrationSection
      ) as AdminTableTypeEnum,
    };
  }

  return {
    kind: "template",
    idWithinKind: dto.templateID,
  };
};

export function mapDtoToUserFilter(dto: UserFilterDTO): UserFilter {
  const { filterJSON, id, type, templateID, administrationSection, ...rest } =
    dto;

  const userFilter: UserFilter = {
    ...rest,
    id: String(id),
    whereItems: JSON.parse(filterJSON),
    tableIdentifier: createTableIdentifierFromFilterDto(dto),
  };

  return userFilter;
}

export function mapDtoArrayOrDictToUserFilterDict(
  dict: NumberIndexedDictionary<UserFilterDTO>
): Dictionary<string, UserFilter> {
  const dtoValues = Object.values(dict);
  const outEntries = dtoValues.map((dto) => [dto.id, mapDtoToUserFilter(dto)]);
  return Object.fromEntries(outEntries);
}

export const getTableIdentifyingDtoPropsFromTableIdentifier = (
  identifier: TableIdentifier
): TableIdentifyingDtoProps => {
  if (identifier.kind === "administration") {
    return {
      type: "administration",
      templateID: null,
      administrationSection: uncapitalizeString(identifier.idWithinKind),
    };
  }

  return {
    type: "template",
    templateID: identifier.idWithinKind,
    administrationSection: null,
  };
};

export function mapUserFilterToDTO(filter: UserFilter): UserFilterDTO {
  const { whereItems, id, tableIdentifier, ...rest } = filter;

  const dto: UserFilterDTO = {
    ...rest,
    ...getTableIdentifyingDtoPropsFromTableIdentifier(tableIdentifier),
    filterJSON: JSON.stringify(whereItems),
    id: Number(id),
  };
  return dto;
}

export function mapPartialUserFilterToPartialDTO(filter: FilterPatch) {
  return produce({} as Partial<UserFilterDTO>, (draft) => {
    const { tableIdentifier, whereItems, id, ...rest } = filter;
    Object.assign(draft, rest);

    if (id) draft.id = Number(id);
    if (tableIdentifier) {
      const identifyingProps =
        getTableIdentifyingDtoPropsFromTableIdentifier(tableIdentifier);
      Object.assign(draft, identifyingProps);
    }
    if (whereItems) {
      draft.filterJSON = JSON.stringify(whereItems);
    }
  });
}

export function matchFilterAgainstIdentifier(
  f: UserFilter | undefined,
  i: TableIdentifier
) {
  return (
    f?.tableIdentifier.kind === i.kind &&
    f?.tableIdentifier.idWithinKind === i.idWithinKind
  );
}
